import React from 'react';
import { Workspace } from '@/types';
import PlayerSearchSelect from './PlayerSearchSelect';
import WorkspaceCheckboxes from './WorkspaceCheckboxes';
import { ROLES, inputClasses, labelClasses } from './types';
import Button from '@/components/ui/Button';

interface EditFormState {
  name: string;
  role: string;
  playerId: string;
  workspaceIds: string[];
}

const UserEditForm: React.FC<{
  editForm: EditFormState;
  setEditForm: (form: EditFormState) => void;
  editingId: string | null;
  currentUserId?: string;
  adminCount: number;
  editError: string;
  editLoading: boolean;
  allPlayers: { id: string; name: string }[];
  workspaces: Workspace[];
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}> = ({
  editForm,
  setEditForm,
  editingId,
  currentUserId,
  adminCount,
  editError,
  editLoading,
  allPlayers,
  workspaces,
  onSubmit,
  onCancel,
}) => (
  <form onSubmit={onSubmit} className="space-y-3">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label htmlFor="edit-user-name" className={labelClasses}>Name</label>
        <input
          id="edit-user-name"
          type="text"
          required
          value={editForm.name}
          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
          className={inputClasses}
        />
      </div>
      <div>
        <label htmlFor="edit-user-role" className={labelClasses}>Role</label>
        <select
          id="edit-user-role"
          value={editForm.role}
          disabled={editingId === currentUserId}
          onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
          className={inputClasses + (editingId === currentUserId ? ' opacity-60 cursor-not-allowed' : '')}
        >
          {ROLES.map((r) => (
            <option key={r} value={r} disabled={r !== 'admin' && editForm.role === 'admin' && adminCount <= 1}>
              {r.charAt(0).toUpperCase() + r.slice(1)}
            </option>
          ))}
        </select>
        {editingId === currentUserId && (
          <p className="mt-1 text-xs text-gray-400">You can't change your own role.</p>
        )}
      </div>
    </div>
    {editForm.role === 'player' && (
      <div>
        <span className={labelClasses}>Link to Player</span>
        <PlayerSearchSelect
          players={allPlayers}
          value={editForm.playerId}
          onChange={(id) => setEditForm({ ...editForm, playerId: id })}
          inputClasses={inputClasses}
        />
      </div>
    )}
    <div>
      <span className={labelClasses}>
        Workspaces
        {editForm.role === 'player' && !editForm.playerId && (
          <span className="text-red-500"> *</span>
        )}
      </span>
      {editForm.role === 'player' && editForm.playerId ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Workspace is inherited from the linked player's squad.
        </p>
      ) : (
        <WorkspaceCheckboxes
          workspaces={workspaces}
          selected={editForm.workspaceIds}
          onChange={(ids) => setEditForm({ ...editForm, workspaceIds: ids })}
        />
      )}
    </div>
    {editError && <div className="text-sm text-red-600 dark:text-red-400">{editError}</div>}
    <div className="flex gap-2">
      <Button
        type="submit"
        loading={editLoading}
        loadingLabel="Saving user"
      >
        Save
      </Button>
      <button
        type="button"
        onClick={onCancel}
        className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
      >
        Cancel
      </button>
    </div>
  </form>
);

export default UserEditForm;
